import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { 
  saveQuestionnaireProgress, 
  completeQuestionnaire, 
  getQuestionnaire, 
  getUserQuestionnaires,
  deleteQuestionnaireDoc,
  getAllCompletedQuestionnaires,
  getAllQuestionnaires
} from './firebase';
import { Page } from '../components/Questionnaire/PageStructureBuilder';
import { WebsiteReference } from '../components/Questionnaire/WebsiteReferenceBuilder';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  website: string;
  projectType: string;
  projectDescription: string;
  projectGoals: string;
  targetAudience: string;
  timeline: string;
  budget: string;
  launchDate: string;
  existingSitemap: string;
  brandDescription: string;
  competitorWebsites: WebsiteReference[];
  inspirationalWebsites: WebsiteReference[];
  dislikedWebsites: WebsiteReference[];
  sitemapFile: string;
  pageStructure: Page[];
  requiredFeatures: string[];
  contentManagement: string;
  ecommerce: string;
  additionalNotes: string;
  projectCategories: string[];
  ecommerceProducts: string;
  ecommercePaymentGateways: string[];
  ecommerceInventory: string;
  ecommerceShipping: string[];
  ecommerceTax: boolean | string;
  ecommerceSubscriptions: boolean | string;
  ecommerceAdditionalNotes: string;
  ecommerceProductCount: string;
  ecommerceCustomization: string;
  ecommerceDiscounts: string;
  ecommerceWholesale: string;
  ecommerceStoreLocator: string;
  ecommerceComments: string;
  // Restaurant specific fields
  restaurantReservations: string;
  restaurantReservationSystem: string;
  restaurantMenuPresentation: string;
  restaurantOtherFeatures: string;
  // Real Estate specific fields
  realEstatePropertyManagement: string;
  realEstatePropertyManagementDetails: string;
  realEstateDisplayUpdates: string;
  realEstateLeadsToCRM: string;
  realEstateDisplayMap: string;
  realEstateMapType: string;
  
  // Hotels/Hospitality specific fields
  hospitalityBookingSystem: string;
  hospitalityBookingSystemDetails: string;
  hospitalityDisplayMap: string;
  hospitalityMapType: string;
  
  // Blog specific fields
  blogUtilization: string;
  blogContentType: string;
  
  // Events specific fields
  eventsSellTickets: string;
  eventsThirdParty: string;
  eventsThirdPartyName: string;
  eventsThirdPartyOther: string;
  eventsRequireRSVP: string;
  
  // Content Management fields - expanded
  contentManagementNeeds: string;
  initialContentUpload: string;
  contentUpdateFrequency: string;
  contentUpdateManagement: string;
  [key: string]: any;
}

interface Questionnaire {
  id: string;
  userId?: string;
  formData: FormData;
  currentStep: number;
  isComplete: boolean;
  createdAt: Date | null;
  updatedAt: Date | null;
  submittedAt: Date | null;
}

export interface QuestionnaireTemplate {
  id?: string;
  name: string;
  title: string;
  description?: string;
  sections: Array<{
    id: string;
    title: string;
    description?: string;
    order: number;
    conditionalRules?: Array<{
      id: string;
      type: 'show' | 'hide' | 'require' | 'skip';
      conditions: Array<{
        questionId: string;
        operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than' | 'in' | 'not_in';
        value: any;
      }>;
    }>;
    subsections?: Array<{
      id: string;
      title: string;
      description?: string;
      order: number;
      conditionalRules?: Array<{
        id: string;
        type: 'show' | 'hide' | 'require' | 'skip';
        conditions: Array<{
          questionId: string;
          operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than' | 'in' | 'not_in';
          value: any;
        }>;
      }>;
      questions: Array<{
        id: string;
        type: string;
        title: string;
        description?: string;
        placeholder?: string;
        required?: boolean;
        accept?: string;
        min?: number;
        max?: number;
        conditionalRules?: Array<{
          id: string;
          type: 'show' | 'hide' | 'require' | 'skip';
          conditions: Array<{
            questionId: string;
            operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than' | 'in' | 'not_in';
            value: any;
          }>;
        }>;
        options?: Array<{
          id: string;
          label: string;
          value: string;
        }>;
      }>;
    }>;
    questions?: Array<{
      id: string;
      type: string;
      title: string;
      description?: string;
      placeholder?: string;
      required?: boolean;
      accept?: string;
      min?: number;
      max?: number;
      conditionalRules?: Array<{
        id: string;
        type: 'show' | 'hide' | 'require' | 'skip';
        conditions: Array<{
          questionId: string;
          operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than' | 'in' | 'not_in';
          value: any;
        }>;
      }>;
      options?: Array<{
        id: string;
        label: string;
        value: string;
      }>;
    }>;
  }>;
  settings: {
    allowSaveProgress: boolean;
    allowBackNavigation: boolean;
    showProgressBar: boolean;
    requireAuthentication: boolean;
  };
}

export interface DashboardSettings {
  welcomeTitle: string;
  welcomeDescription: string;
  startButtonText: string;
}

interface QuestionnaireContextType {
  currentQuestionnaire: Questionnaire | null;
  userQuestionnaires: Questionnaire[];
  allCompletedQuestionnaires: Questionnaire[];
  allQuestionnaires: Questionnaire[];
  activeTemplate: QuestionnaireTemplate | null;
  dashboardSettings: DashboardSettings | null;
  loading: boolean;
  isAdmin: boolean;
  createQuestionnaire: () => Promise<string>;
  loadQuestionnaire: (id: string) => Promise<void>;
  loadActiveTemplate: (forceFresh?: boolean) => Promise<void>;
  loadDashboardSettings: () => Promise<void>;
  updateDashboardSettings: (settings: DashboardSettings) => Promise<void>;
  updateQuestionnaireData: (data: Partial<FormData>, step?: number) => Promise<void>;
  submitQuestionnaire: () => Promise<void>;
  loadUserQuestionnaires: () => Promise<void>;
  loadAllCompletedQuestionnaires: () => Promise<void>;
  loadAllQuestionnaires: () => Promise<void>;
  deleteQuestionnaire: (id: string) => Promise<void>;
}

const initialFormData: FormData = {
  // Contact Information
  name: '',
  email: '',
  phone: '',
  company: '',
  website: '',
  
  // Project Information
  projectType: 'website', // Default to website
  projectDescription: '',
  projectGoals: '',
  targetAudience: '',
  
  // Timeline & Budget
  timeline: '',
  budget: '',
  launchDate: '',
  
  // Page Structure & Sitemap
  existingSitemap: 'no', // Default to 'no'
  brandDescription: '',
  competitorWebsites: [],
  inspirationalWebsites: [],
  dislikedWebsites: [],
  sitemapFile: '',
  pageStructure: [],
  
  // Features & Functionality
  requiredFeatures: [],
  contentManagement: '',
  ecommerce: '',
  additionalNotes: '',
  
  // Project Categories
  projectCategories: [],
  
  // E-commerce specific fields
  ecommerceProducts: '',
  ecommercePaymentGateways: [],
  ecommerceInventory: '',
  ecommerceShipping: [],
  ecommerceTax: false,
  ecommerceSubscriptions: false,
  ecommerceAdditionalNotes: '',
  ecommerceProductCount: '',
  ecommerceCustomization: '',
  ecommerceDiscounts: '',
  ecommerceWholesale: '',
  ecommerceStoreLocator: '',
  ecommerceComments: '',
  
  // Restaurant specific fields
  restaurantReservations: '',
  restaurantReservationSystem: '',
  restaurantMenuPresentation: '',
  restaurantOtherFeatures: '',
  
  // Real Estate specific fields
  realEstatePropertyManagement: '',
  realEstatePropertyManagementDetails: '',
  realEstateDisplayUpdates: '',
  realEstateLeadsToCRM: '',
  realEstateDisplayMap: '',
  realEstateMapType: '',
  
  // Hotels/Hospitality specific fields
  hospitalityBookingSystem: '',
  hospitalityBookingSystemDetails: '',
  hospitalityDisplayMap: '',
  hospitalityMapType: '',
  
  // Blog specific fields
  blogUtilization: '',
  blogContentType: '',
  
  // Events specific fields
  eventsSellTickets: '',
  eventsThirdParty: '',
  eventsThirdPartyName: '',
  eventsThirdPartyOther: '',
  eventsRequireRSVP: '',
  
  // Content Management fields - expanded
  contentManagementNeeds: '',
  initialContentUpload: 'default',
  contentUpdateFrequency: '',
  contentUpdateManagement: '',
};

const QuestionnaireContext = createContext<QuestionnaireContextType | null>(null);

export const useQuestionnaire = () => {
  const context = useContext(QuestionnaireContext);
  if (!context) {
    throw new Error('useQuestionnaire must be used within a QuestionnaireProvider');
  }
  return context;
};

export const QuestionnaireProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [currentQuestionnaire, setCurrentQuestionnaire] = useState<Questionnaire | null>(null);
  const [userQuestionnaires, setUserQuestionnaires] = useState<Questionnaire[]>([]);
  const [allCompletedQuestionnaires, setAllCompletedQuestionnaires] = useState<Questionnaire[]>([]);
  const [allQuestionnaires, setAllQuestionnaires] = useState<Questionnaire[]>([]);
  const [activeTemplate, setActiveTemplate] = useState<QuestionnaireTemplate | null>(null);
  const [dashboardSettings, setDashboardSettings] = useState<DashboardSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Memoize the functions to prevent infinite loops
  const loadUserQuestionnaires = useCallback(async (): Promise<void> => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const questionnaires = await getUserQuestionnaires(currentUser.uid);
      setUserQuestionnaires(questionnaires);
    } catch (error) {
      console.error('Error loading user questionnaires:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const loadAllCompletedQuestionnaires = useCallback(async (): Promise<void> => {
    if (!currentUser || !isAdmin) return;
    
    setLoading(true);
    try {
      const questionnaires = await getAllCompletedQuestionnaires();
      setAllCompletedQuestionnaires(questionnaires);
    } catch (error) {
      console.error('Error loading all completed questionnaires:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const loadAllQuestionnaires = useCallback(async (): Promise<void> => {
    if (!currentUser) {
      console.log('Cannot load questionnaires: No current user');
      return;
    }
    
    console.log('Loading all questionnaires, currentUser=', currentUser.email, 'isAdmin=', isAdmin);
    
    setLoading(true);
    try {
      const questionnaires = await getAllQuestionnaires();
      console.log('Loaded questionnaires:', questionnaires.length);
      setAllQuestionnaires(questionnaires);
    } catch (error) {
      console.error('Error loading all questionnaires:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser, isAdmin]);

  const loadActiveTemplate = useCallback(async (forceFresh: boolean = false): Promise<void> => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const token = await currentUser.getIdToken();
      
      // Add cache busting parameter to force fresh template when needed
      const cacheBuster = forceFresh ? `?_t=${Date.now()}` : '';
      const url = `https://api-reuvze3ckq-uc.a.run.app/api/questionnaire-templates/active${cacheBuster}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': forceFresh ? 'no-cache, no-store, must-revalidate' : ''
        }
      });
      
      if (response.ok) {
        const template = await response.json();
        setActiveTemplate(template);
        console.log('Active template loaded', forceFresh ? '(fresh)' : '(cached)', template.name);
      } else {
        console.error('Failed to load active template:', response.statusText);
      }
    } catch (error) {
      console.error('Error loading active template:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const loadDashboardSettings = useCallback(async (): Promise<void> => {
    if (!currentUser) return;
    
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`https://api-reuvze3ckq-uc.a.run.app/api/settings/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const settings = await response.json();
        setDashboardSettings(settings);
      } else {
        console.error('Failed to load dashboard settings:', response.statusText);
      }
    } catch (error) {
      console.error('Error loading dashboard settings:', error);
    }
  }, [currentUser]);

  const updateDashboardSettings = useCallback(async (settings: DashboardSettings): Promise<void> => {
    if (!currentUser) return;
    
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(`https://api-reuvze3ckq-uc.a.run.app/api/settings/dashboard`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        setDashboardSettings(settings);
      } else {
        console.error('Failed to update dashboard settings:', response.statusText);
        throw new Error('Failed to update dashboard settings');
      }
    } catch (error) {
      console.error('Error updating dashboard settings:', error);
      throw error;
    }
  }, [currentUser]);

  // Add template cache clearing function to window object
  useEffect(() => {
    // Add a global function to clear template cache
    (window as any).clearTemplateCache = () => {
      console.log('Clearing template cache');
      setActiveTemplate(null);
    };
    
    return () => {
      delete (window as any).clearTemplateCache;
    };
  }, []);

  // Check if user is admin (has @sdcopartners.com or @stitchdesignco.com email)
  useEffect(() => {
    if (currentUser?.email) {
      const email = currentUser.email.toLowerCase();
      console.log('Checking admin status for email:', email);
      
      // Only allow specific email domains for admin access
      const devMode = false; // Set to false in production
      const isAdminEmail = devMode ? true : (email.endsWith('@sdcopartners.com') || email.endsWith('@stitchdesignco.com'));
      console.log('Is admin email?', isAdminEmail);
      
      // Update admin status if needed
      if (isAdminEmail && !isAdmin) {
        console.log('Setting admin status to true');
        setIsAdmin(true);
      } else if (!isAdminEmail && isAdmin) {
        setIsAdmin(false);
        console.log('Setting admin status to false');
      } else {
        // Status is not changing, but log it
        console.log('Admin status unchanged:', isAdminEmail);
      }
    } else {
      console.log('No user email available, setting isAdmin to false');
      setIsAdmin(false);
    }
  }, [currentUser, isAdmin]);

  // Load user questionnaires when user logs in
  useEffect(() => {
    if (currentUser) {
      loadUserQuestionnaires();
    } else {
      setUserQuestionnaires([]);
      setCurrentQuestionnaire(null);
    }
  }, [currentUser, loadUserQuestionnaires]);

  const createQuestionnaire = async (): Promise<string> => {
    if (!currentUser) throw new Error('User must be logged in to create a questionnaire');
    
    console.log('QuestionnaireContext: Creating new questionnaire');
    setLoading(true);
    try {
      // Ensure projectType is set to 'website'
      const formDataWithWebsite = {
        ...initialFormData,
        projectType: 'website'
      };
      
      const questionnaireData = {
        formData: formDataWithWebsite,
        currentStep: 0,
        templateId: activeTemplate?.id || 'default', // Add template ID for email notifications
      };
      
      console.log('QuestionnaireContext: Saving questionnaire progress');
      const questionnaireId = await saveQuestionnaireProgress(
        currentUser.uid, 
        null, 
        questionnaireData
      );
      console.log('QuestionnaireContext: Got questionnaire ID:', questionnaireId);
      
      const newQuestionnaire: Questionnaire = {
        id: questionnaireId,
        formData: formDataWithWebsite,
        currentStep: 0,
        isComplete: false,
        createdAt: new Date(),
        updatedAt: new Date(),
        submittedAt: null
      };
      
      console.log('QuestionnaireContext: Setting current questionnaire');
      setCurrentQuestionnaire(newQuestionnaire);
      setLoading(false);
      
      return questionnaireId;
    } catch (error) {
      setLoading(false);
      console.error('QuestionnaireContext: Error creating questionnaire:', error);
      throw error;
    }
  };

  const loadQuestionnaire = async (id: string): Promise<void> => {
    if (!currentUser) throw new Error('User must be logged in to load a questionnaire');
    
    console.log('QuestionnaireContext: Loading questionnaire with ID:', id);
    setLoading(true);
    try {
      const questionnaireData = await getQuestionnaire(currentUser.uid, id);
      console.log('QuestionnaireContext: Questionnaire data received:', questionnaireData);
      
      if (questionnaireData) {
        setCurrentQuestionnaire(questionnaireData as Questionnaire);
        console.log('QuestionnaireContext: Current questionnaire set');
      } else {
        console.log('QuestionnaireContext: Questionnaire not found');
        throw new Error('Questionnaire not found');
      }
    } catch (error) {
      console.error('QuestionnaireContext: Error loading questionnaire:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Fix the updateQuestionnaireData function to match the interface
  const updateQuestionnaireData = useCallback(async (data: Partial<FormData>, step?: number): Promise<void> => {
    if (!currentUser) {
      throw new Error('User must be logged in to update a questionnaire');
    }
    
    setLoading(true);
    
    try {
      if (!currentQuestionnaire) {
        throw new Error('No questionnaire loaded');
      }
      
      // Merge the new data with existing data
      const updatedFormData = {
        ...currentQuestionnaire.formData,
        ...data,
        // Deep clone the pageStructure array if it exists in the new data
        ...(data.pageStructure ? { 
          pageStructure: JSON.parse(JSON.stringify(data.pageStructure)) 
        } : {})
      };
      
      // Save progress to Firestore
      await saveQuestionnaireProgress(
        currentUser.uid,
        currentQuestionnaire.id,
        {
          formData: updatedFormData,
          currentStep: step !== undefined ? step : currentQuestionnaire.currentStep
        }
      );
      
      // Update local state
      setCurrentQuestionnaire({
        ...currentQuestionnaire,
        formData: updatedFormData,
        currentStep: step !== undefined ? step : currentQuestionnaire.currentStep
      });
      
    } catch (error) {
      console.error('Error updating questionnaire data:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [currentUser, currentQuestionnaire, setLoading]);

  const submitQuestionnaire = async (): Promise<void> => {
    if (!currentUser || !currentQuestionnaire) {
      throw new Error('User must be logged in and a questionnaire must be active');
    }
    
    setLoading(true);
    try {
      console.log('Submitting questionnaire with data:', currentQuestionnaire.formData);
      
      // Calculate time spent (in seconds) - use a reasonable default if startedAt is not available
      const timeSpent = currentQuestionnaire.createdAt 
        ? Math.floor((Date.now() - currentQuestionnaire.createdAt.getTime()) / 1000)
        : 300; // Default to 5 minutes if no creation time available
      
      await completeQuestionnaire(
        currentUser.uid,
        currentQuestionnaire.id,
        {
          formData: currentQuestionnaire.formData,
          currentStep: currentQuestionnaire.currentStep,
          timeSpent: timeSpent,
          templateId: activeTemplate?.id || 'default'
        }
      );
      
      setCurrentQuestionnaire({
        ...currentQuestionnaire,
        isComplete: true,
        submittedAt: new Date()
      });
      
      // Refresh the user questionnaires list
      await loadUserQuestionnaires();
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteQuestionnaire = async (id: string): Promise<void> => {
    if (!currentUser) throw new Error('User must be logged in to delete a questionnaire');
    
    setLoading(true);
    try {
      await deleteQuestionnaireDoc(currentUser.uid, id);
      // Refresh the user questionnaires list
      await loadUserQuestionnaires();
    } catch (error) {
      console.error('Error deleting questionnaire:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    currentQuestionnaire,
    userQuestionnaires,
    allCompletedQuestionnaires,
    allQuestionnaires,
    activeTemplate,
    dashboardSettings,
    loading,
    isAdmin,
    createQuestionnaire,
    loadQuestionnaire,
    loadActiveTemplate,
    loadDashboardSettings,
    updateDashboardSettings,
    updateQuestionnaireData,
    submitQuestionnaire,
    loadUserQuestionnaires,
    loadAllCompletedQuestionnaires,
    loadAllQuestionnaires,
    deleteQuestionnaire
  };

  return (
    <QuestionnaireContext.Provider value={value}>
      {children}
    </QuestionnaireContext.Provider>
  );
}; 